// Finance-side "Enter a bill" modal (AP entry). The first path for keying
// a vendor bill that isn't tied to a PO line — Stephanie's #1 daily task.
//
// POSTs to /bills with ingestion_source='manual' and
// initial_status='pending_review' so the new bill lands in the AP grid
// awaiting approval (she approves / pays it on the bill detail page).
// Totals are seeded directly (the server stores the passed subtotal / tax
// / shipping / total — bill_line_items would otherwise roll them up, but
// header-only entry skips lines).
//
// PO-linked entry (matching a bill to its PO) stays on the per-line Attach
// flow + a future PO-side "enter vendor bill" button; this is the orphan /
// quick-entry path the Definition of Done calls for.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { TermsDueHint, useTermsDueDate } from "@/components/finance/TermsDueHint";
import { MentionTextarea } from "@/components/notes/MentionTextarea";
import { SupplierBillFile, type AttachedFile } from "@/components/finance/SupplierBillFile";

interface Vendor {
  id: string;
  name: string;
  paymentTerms?: string | null;
}

interface CreatedBill {
  id: string;
  vendorName?: string;
}

interface Props {
  onCancel: () => void;
  onCreated: (bill: CreatedBill) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/** Parse a loose numeric input ("1,250.50") → number, NaN if empty/bad. */
function parseAmount(s: string): number {
  const cleaned = s.replace(/[$,\s]/g, "");
  if (cleaned === "") return NaN;
  return Number(cleaned);
}

const FIELD =
  "block h-10 w-full rounded border border-rule bg-white px-3 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";

// iPhone Safari draws date boxes wider than the screen unless told otherwise.
const DATE_FIELD = `${FIELD} min-w-0 max-w-full appearance-none text-left`;

function extractError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    // The database refuses a second bill with the same number from the same
    // vendor (unique vendor_id + vendor_bill_number) — say so in plain words.
    if (err.status === 409) {
      return "This vendor already has a bill with that number, so it wasn't saved. Check the number — if it's the same bill, it's already in Billing.";
    }
    const body = (err.body ?? {}) as { error?: string };
    return body.error ?? `Couldn't save the bill (${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "Couldn't save the bill";
}

export function EnterBillModal({ onCancel, onCreated }: Props) {
  const vendorsQ = useQuery({
    queryKey: ["vendors", "all"],
    queryFn: () => api<{ rows: Vendor[] }>("/vendors"),
    staleTime: 5 * 60 * 1000,
  });
  const vendors = vendorsQ.data?.rows ?? [];

  const [vendorId, setVendorId] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(todayIso());
  const picked = vendors.find((v) => v.id === vendorId) ?? null;
  // Due date from the vendor's terms until someone types one (Stephanie 2026-10-20).
  const { dueDate, setDueDate, fromTerms } = useTermsDueDate(billDate, picked?.paymentTerms);
  const [subtotal, setSubtotal] = useState("");
  const [tax, setTax] = useState("");
  const [shipping, setShipping] = useState("");
  const [notes, setNotes] = useState("");
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  // The supplier's bill (Stephanie 2026-10-22).
  const [billFile, setBillFile] = useState<AttachedFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Total is derived — amount owed = subtotal + tax + shipping. Keeps the
  // stored breakdown internally consistent (no free-floating total field
  // that can disagree with its parts).
  const subtotalNum = parseAmount(subtotal);
  const taxNum = parseAmount(tax);
  const shippingNum = parseAmount(shipping);
  const total = useMemo(() => {
    const sub = Number.isFinite(subtotalNum) ? subtotalNum : 0;
    const t = Number.isFinite(taxNum) ? taxNum : 0;
    const sh = Number.isFinite(shippingNum) ? shippingNum : 0;
    return Math.round((sub + t + sh) * 100) / 100;
  }, [subtotalNum, taxNum, shippingNum]);

  const createMutation = useMutation({
    mutationFn: () =>
      api<{ bill: { id: string } }>("/bills", {
        method: "POST",
        body: JSON.stringify({
          vendor_id: vendorId,
          vendor_bill_number: billNumber.trim(),
          bill_date: billDate,
          due_date: dueDate || null,
          subtotal: (Number.isFinite(subtotalNum) ? subtotalNum : 0).toFixed(2),
          tax: (Number.isFinite(taxNum) ? taxNum : 0).toFixed(2),
          shipping_cost: (Number.isFinite(shippingNum) ? shippingNum : 0).toFixed(2),
          total: total.toFixed(2),
          notes: notes.trim() || null,
          mention_user_ids: noteMentions,
          file_id: billFile?.fileId ?? null,
          ingestion_source: "manual",
          initial_status: "pending_review",
        }),
      }),
    onSuccess: (res) => {
      const vendorName = vendors.find((v) => v.id === vendorId)?.name;
      onCreated({ id: res.bill.id, vendorName });
    },
  });

  // Close on ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !createMutation.isPending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createMutation.isPending, onCancel]);

  function handleSubmit() {
    setLocalError(null);
    if (!vendorId) {
      setLocalError("Pick a vendor.");
      return;
    }
    // Required (Stephanie 2026-11-03: "the supplier's bill number should be required not optional").
    if (!billNumber.trim()) {
      setLocalError("Enter the supplier's bill number.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(billDate)) {
      setLocalError("Bill date is required.");
      return;
    }
    if (subtotal.trim() !== "" && !Number.isFinite(subtotalNum)) {
      setLocalError("Subtotal must be a number.");
      return;
    }
    if (tax.trim() !== "" && !Number.isFinite(taxNum)) {
      setLocalError("Tax must be a number.");
      return;
    }
    if (shipping.trim() !== "" && !Number.isFinite(shippingNum)) {
      setLocalError("Shipping must be a number.");
      return;
    }
    if (!(total > 0)) {
      setLocalError("Enter an amount — the total must be greater than zero.");
      return;
    }
    createMutation.mutate();
  }

  const error = localError ?? extractError(createMutation.error);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Enter a bill"
    >
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-md bg-white shadow-xl sm:rounded-md">
        <div className="flex items-start justify-between gap-4 border-b border-rule px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
              Enter a bill
            </h2>
            <p className="mt-1 text-xs text-muted">
              Key a vendor bill into AP. It lands as “to review” so you can
              check it and approve it before paying.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={createMutation.isPending}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted hover:bg-canvas hover:text-ink disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="flex-1 overflow-y-auto px-6 py-4"
        >
          <div className="space-y-4">
            <Labeled label="Vendor" required>
              <select
                autoFocus
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                disabled={createMutation.isPending || vendorsQ.isLoading}
                className={FIELD}
              >
                <option value="">
                  {vendorsQ.isLoading ? "Loading vendors…" : "Select a vendor…"}
                </option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </Labeled>

            <div className="grid grid-cols-2 gap-4">
              <Labeled label="Supplier's bill #" required>
                <input
                  value={billNumber}
                  onChange={(e) => setBillNumber(e.target.value)}
                  maxLength={120}
                  required
                  aria-required="true"
                  placeholder="From the supplier's bill"
                  className={FIELD}
                />
              </Labeled>
              <Labeled label="Bill date" required>
                <input
                  type="date"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  className={DATE_FIELD}
                />
              </Labeled>
            </div>

            <Labeled label="Due date">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={DATE_FIELD}
              />
              <TermsDueHint vendorName={picked?.name ?? null} terms={picked?.paymentTerms} fromTerms={fromTerms} />
            </Labeled>

            <div className="grid grid-cols-3 gap-4">
              <Labeled label="Subtotal" required>
                <input
                  inputMode="decimal"
                  value={subtotal}
                  onChange={(e) => setSubtotal(e.target.value)}
                  placeholder="0.00"
                  className={`${FIELD} text-right tabular-nums`}
                />
              </Labeled>
              <Labeled label="Tax">
                <input
                  inputMode="decimal"
                  value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  placeholder="0.00"
                  className={`${FIELD} text-right tabular-nums`}
                />
              </Labeled>
              <Labeled label="Shipping">
                <input
                  inputMode="decimal"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  placeholder="0.00"
                  className={`${FIELD} text-right tabular-nums`}
                />
              </Labeled>
            </div>

            <div className="flex items-center justify-between rounded-md border border-rule bg-canvas px-4 py-3">
              <span className="text-sm text-muted">
                Total owed
              </span>
              <span className="text-lg font-semibold tabular-nums text-ink">
                {money(total)}
              </span>
            </div>

            <Labeled label="Supplier's bill">
              <SupplierBillFile value={billFile} onChange={setBillFile} prefix="bills/supplier" onBusy={setUploading} />
            </Labeled>

            <Labeled label="Notes">
              <MentionTextarea
                value={notes}
                onChange={(t, ids) => { setNotes(t); setNoteMentions(ids); }}
                rows={2}
                maxLength={2000}
                placeholder="Optional — PO reference, what this bill covers, etc. — type @ to tag someone"
                className="block w-full rounded border border-rule bg-white px-3 py-2 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]"
              />
            </Labeled>

            {error && (
              <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-sm text-risk">
                {error}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || uploading}>
              {createMutation.isPending ? "Saving…" : "Enter bill"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Labeled({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted">
        {label}
        {required && <span className="ml-1">(required)</span>}
      </span>
      {children}
    </label>
  );
}
